// ============================================================================
// ACTION BUTTON FACTORY — canonical header buttons, every screen
// ============================================================================
// Returns pre-configured `ActionButton` objects that match the enterprise UI
// standards (size 18, variant 'icon', tooltip set, IconSystem icon). Use
// these instead of building ActionButton literals by hand — they enforce
// the standards by construction.
//
// USAGE (in a screen):
//   const actionButtons: ActionButton[] = [
//     actionButtonFactory.search(handleSearch),
//     actionButtonFactory.add(handleAdd, 'Add new service'),
//     actionButtonFactory.refresh(refetch),
//     actionButtonFactory.export_(handleExport),
//   ];
//   <ModuleHeader actions={actionButtons} ... />

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from '../modals/common/ModuleHeader';

type Handler = () => void | Promise<void>;

interface FactoryOpts {
  /** Override default tooltip text. */
  tooltip?: string;
  /** When false, the button is hidden from the toolbar. Default true. */
  visible?: boolean;
  /** Disable the button (e.g. during loading). Default false. */
  disabled?: boolean;
  /** Override the default id. Useful when a screen has multiple of one kind (e.g. two refresh buttons). */
  id?: string;
  /** Override the default label. */
  label?: string;
}

const make = (
  id: string,
  iconName: keyof typeof IconSystem,
  defaultLabel: string,
  defaultTooltip: string,
  onClick: Handler,
  opts: FactoryOpts = {},
): ActionButton => {
  const Icon = IconSystem[iconName] as React.ComponentType<{ size?: number }>;
  return {
    id:       opts.id      ?? id,
    icon:     <Icon size={18} />,
    label:    opts.label   ?? defaultLabel,
    onClick:  () => { void onClick(); },
    tooltip:  opts.tooltip ?? defaultTooltip,
    variant:  'icon',
    visible:  opts.visible ?? true,
    disabled: opts.disabled ?? false,
  };
};

/**
 * Pre-configured `ActionButton` builders matching the enterprise UI standards.
 * Every entry returns an `ActionButton` with `variant: 'icon'`, `size={18}`,
 * and an appropriate tooltip — so screens can't accidentally ship a header
 * with size-16 icons or primary-pill buttons.
 */
export const actionButtonFactory = {
  search:    (onClick: Handler, tooltip?: string) => make('search',   'search',     'Search',   tooltip ?? 'Search (F2)',  onClick),
  add:       (onClick: Handler, tooltip?: string) => make('add',      'add',        'Add New',  tooltip ?? 'Add new',      onClick),
  edit:      (onClick: Handler, tooltip?: string) => make('edit',     'edit',       'Edit',     tooltip ?? 'Edit',         onClick),
  refresh:   (onClick: Handler, tooltip?: string) => make('refresh',  'refresh',    'Refresh',  tooltip ?? 'Refresh',      onClick),
  export_:   (onClick: Handler, tooltip?: string) => make('export',   'download',   'Export',   tooltip ?? 'Export',       onClick),
  import_:   (onClick: Handler, tooltip?: string) => make('import',   'upload',     'Import',   tooltip ?? 'Import',       onClick),
  print:     (onClick: Handler, tooltip?: string) => make('print',    'print',      'Print',    tooltip ?? 'Print',        onClick),
  save:      (onClick: Handler, tooltip?: string) => make('save',     'save',       'Save',     tooltip ?? 'Save',         onClick),
  delete_:   (onClick: Handler, tooltip?: string) => make('delete',   'trash',      'Delete',   tooltip ?? 'Delete',       onClick),
  copy:      (onClick: Handler, tooltip?: string) => make('copy',     'copy',       'Copy',     tooltip ?? 'Copy',         onClick),
  view:      (onClick: Handler, tooltip?: string) => make('view',     'eye',        'View',     tooltip ?? 'View',         onClick),

  /**
   * Escape hatch for a custom action that still wants the standard styling
   * (variant: 'icon', size 18). For domain-specific actions like SUBMIT /
   * APPROVE / RECALL — pick a sensible IconSystem icon.
   */
  custom: (
    id: string,
    icon: React.ReactNode,
    label: string,
    onClick: Handler,
    opts: FactoryOpts = {},
  ): ActionButton => ({
    id,
    icon,
    label,
    onClick: () => { void onClick(); },
    tooltip:  opts.tooltip ?? label,
    variant:  'icon',
    visible:  opts.visible  ?? true,
    disabled: opts.disabled ?? false,
  }),
} as const;

export default actionButtonFactory;
